import ExampleTheme from "./themes/ExampleTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import TreeViewPlugin from "./plugins/TreeViewPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import { $getRoot, CLEAR_HISTORY_COMMAND, TextNode } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import SerializeHtmlPlugin from "./plugins/SerializeHtmlPlugin";
import { TextareaNode, TextareaPlugin } from "./plugins/TextBoxPlugin";
import { useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { FormHelperText } from "@mui/material";
import { ExtendedTextNode } from "./plugins/ExtendedTextNodePlugin";

function Placeholder() {
  return <div className="editor-placeholder">Enter some text...</div>;
}

// A component (plugin) with a button that updates the state of lexical. As long
// as this component is used within LexicalComposer it will work. Within
// LexicalComposer one can use any layout, for example cols, rows, divs.
const ResetStatePlugin = ({ initialState, resetTextEditorRef }) => {
  const [editor] = useLexicalComposerContext();

  const onButtonClick = (e) => {
    e.preventDefault()

    if (!initialState) {
      return
    }

    // reset editor to initial state and clear history
    editor.update(() => {
      const initialEditorState = editor.parseEditorState(initialState.toJSON())
      editor.setEditorState(initialEditorState)
      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
    })
  };

  return <button style={{ display: "none" }} ref={resetTextEditorRef} onClick={onButtonClick} />;
};

export default function Editor({
  jsonTextEditor,
  setJsonTextEditor,
  rawHtmlTextEditor,
  setRawHtmlTextEditor,
  setPlainTextEditor,
  resetTextEditorRef,
  error,
  autoFocus = false,
  isNotificationEditor = false
}) {
  const [initialState, setInitialState] = useState(null)

  const editorConfig = {
    editorState: jsonTextEditor,
    // The editor theme
    theme: ExampleTheme,
    // Handling of errors during update
    onError(error) {
      throw error;
    },
    // Any custom nodes go here
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      ExtendedTextNode,
      { replace: TextNode, with: (node) => new ExtendedTextNode(node.__text) },
      // TextareaNode
    ]
  };

  // When the editor changes, you can get notified via the
  // OnChangePlugin!
  function onChange(editorState, editor) {
    editor.update(() => {
      const rawHTML = $generateHtmlFromNodes(editor, null)
      const editorStateTextString = editorState.read(() => $getRoot().getTextContent());

      setRawHtmlTextEditor(rawHTML)
      setJsonTextEditor(editorState)
      setPlainTextEditor(editorStateTextString)

      if (!initialState) {
        setInitialState(editorState)
      }
    });
  }

  // TODO: TextArea is buggy, the recommended solution is to use a nested editor. But how would this play with the html import and export?
  // TODO: Add markdown func for checkbox []

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="editor-container">
        <ToolbarPlugin isNotificationEditor={isNotificationEditor} error={Boolean(error)} />
        <div className="editor-inner" style={{ borderColor: error && "#d32f2f" }} data-cy="rich-text-editor-input">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          {/* <TreeViewPlugin /> // * DEBUGGER */}
          {autoFocus && <AutoFocusPlugin />}
          {/* <CodeHighlightPlugin /> */}
          <ListPlugin />
          <CheckListPlugin />
          {/* <TextareaPlugin /> */}
          <LinkPlugin />
          <AutoLinkPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin onChange={onChange} />
          <SerializeHtmlPlugin
            rawHtml={rawHtmlTextEditor}
            setJsonTextEditor={setJsonTextEditor}
            initialState={initialState}
            setInitialState={setInitialState}
            setPlainTextEditor={setPlainTextEditor}
          />
          <ResetStatePlugin initialState={initialState} resetTextEditorRef={resetTextEditorRef} />
        </div>
      </div>
      <FormHelperText sx={{ color: "#d32f2f", mt: -2, ml: 2 }} >{Boolean(error) && error?.message}</FormHelperText>
    </LexicalComposer>
  );
}
